import { ComponentProps, FC, useCallback, useEffect, useState } from "react"
import { userTempPasswordCreateService } from "src/domain/services/user/userTempPasswordCreateService"
import { useAuth } from "src/hooks"
import { FormikOnSubmit } from "src/utils/types"
import {
	IUserTempPasswordDrawerFormFields,
	UserTempPasswordDrawerFormView,
} from "."

type Props = Omit<
	ComponentProps<typeof UserTempPasswordDrawerFormView>,
	| "handleSubmit"
	| "tempPassword"
	| "tempPasswordExpiresAt"
	| "onCopyPassword"
	| "isCopyDisabled"
	| "isCopied"
> & {
	onSuccess?: () => void
}

export const UserTempPasswordDrawerFormController: FC<Props> = ({
	onSuccess,
	...rest
}) => {
	const { token } = useAuth()
	const [tempPassword, setTempPassword] = useState<string>()
	const [tempPasswordExpiresAt, setTempPasswordExpiresAt] = useState<number>()
	const [isCopied, setIsCopied] = useState(false)

	useEffect(() => {
		if (!rest.isOpen) {
			setIsCopied(false)
		}
	}, [rest.isOpen])

	const handleSubmit = useCallback<FormikOnSubmit<IUserTempPasswordDrawerFormFields>>(
		async (_, { setErrors }) => {
			try {
				const response = await userTempPasswordCreateService(token)
				setTempPassword(response.tempPassword)
				setTempPasswordExpiresAt(response.tempPasswordExpiresAt)
				setIsCopied(false)
				onSuccess?.()
			} catch (err) {
				const message =
					err instanceof Error ? err.message : "Something went wrong"
				setErrors({ action: message })
			}
		},
		[token, onSuccess],
	)

	const handleCopyPassword = useCallback(async () => {
		if (!tempPassword) return
		await navigator.clipboard.writeText(tempPassword)
		setIsCopied(true)
	}, [tempPassword])

	return (
		<UserTempPasswordDrawerFormView
			handleSubmit={handleSubmit}
			tempPassword={tempPassword}
			tempPasswordExpiresAt={tempPasswordExpiresAt}
			onCopyPassword={handleCopyPassword}
			isCopyDisabled={!tempPassword}
			isCopied={isCopied}
			{...rest}
		/>
	)
}
